import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import { useMailboxes } from '../hooks/useMailboxes';
import type { CampaignDetail } from '../outreachTypes';
import { shortDate } from '../outreachFormat';
import { runsUntilOf } from './builderFormat';
import type { BuilderStep } from './builderSteps';
import { planRate } from './planRates';
import { campaignMessages as messages } from './campaignMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BuilderSummaryProps {
  accountId: number;
  campaign: CampaignDetail;
  step: BuilderStep;
}

/** "This campaign" (design outreach-v2 b1 to e1, right column): the facts that the steps set. */
const BuilderSummaryComponent: React.FC<BuilderSummaryProps> = ({ accountId, campaign, step }) => {
  const intl = useIntl();
  const { mailboxes } = useMailboxes(accountId);
  const rows = useMemo(() => {
    const until = runsUntilOf(campaign, new Date());
    const from = mailboxes.find((mailbox) => campaign.mailboxUuids.includes(mailbox.mailboxUuid));
    return [
      { key: 'song', label: messages.summarySong, value: campaign.songTitle },
      { key: 'out', label: messages.summaryOut, value: shortDate(intl, campaign.releaseDate) },
      { key: 'fit', label: messages.summaryFitNow, value: intl.formatNumber(campaign.poolTotal) },
      { key: 'kinds', label: messages.summaryKinds, value: String(campaign.kinds.length) },
      { key: 'perDay', label: messages.summaryPerDay, value: String(campaign.dailyRate) },
      {
        key: 'followUps',
        label: messages.summaryFollowUps,
        value: intl.formatMessage(messages.summaryFollowUpsValue, { on: String(campaign.followUpDays !== null) }),
      },
      { key: 'from', label: messages.summarySendFrom, value: from?.address ?? '—' },
      {
        key: 'until',
        label: messages.summaryRunsUntil,
        value: until === null ? intl.formatMessage(messages.summaryUntilStop) : shortDate(intl, until.toISOString()),
      },
    ].map((row) => (
      <div key={row.key} className={styles.sumRow} data-testid={`summary-${row.key}`}>
        <span>{intl.formatMessage(row.label)}</span>
        <b title={row.value}>{row.value}</b>
      </div>
    ));
  }, [campaign, intl, mailboxes]);

  const note = () => {
    if (step === 'song') {
      return intl.formatMessage(messages.summaryRateNote, { rate: campaign.dailyRate, label: planRate('label') });
    }
    return intl.formatMessage(step === 'who' ? messages.summaryNamesNote : messages.summaryPage);
  };

  return (
    <Panel as="aside">
      <PanelHead title={intl.formatMessage(messages.summaryTitle)} />
      <PanelBody>
        <div className={styles.sum} data-testid="builder-summary">
          {rows}
        </div>
        <div className={styles.hair} />
        <p className={shared.small}>{note()}</p>
      </PanelBody>
    </Panel>
  );
};

export const BuilderSummary = TypedMemo(BuilderSummaryComponent);
